import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";

const buildPath = (lang, hash = "") => `/${lang}${hash}`;

export default function Header() {
  const { lang, t, changeLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const nav = [
    { key: "home", href: buildPath(lang) },
    { key: "how", href: buildPath(lang, "#how") },
    { key: "projects", href: buildPath(lang, "#projects") },
    { key: "about", href: buildPath(lang, "#about") },
  ];

  const scrollToHash = (hash) => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
  };

  const handleNavClick = (e, href) => {
    setOpen(false);
    const url = new URL(href, window.location.origin);
    if (url.pathname === location.pathname && url.hash) {
      e.preventDefault();
      window.history.replaceState(null, "", href);
      scrollToHash(url.hash);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full bg-white border-b border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <NavLink
          to={buildPath(lang)}
          data-testid="header-logo"
          className="flex items-center gap-3"
        >
          <span
            aria-hidden="true"
            className="inline-flex h-9 w-9 items-center justify-center rounded-sm bg-slate-900 text-white font-serif text-lg"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            H
          </span>
          <span className="flex flex-col leading-tight">
            <span
              className="text-lg text-slate-900 font-medium"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("header.logoName")}
            </span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-slate-500">
              {t("header.logoTagline")}
            </span>
          </span>
        </NavLink>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((item) => (
            <a
              key={item.key}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              data-testid={`nav-${item.key}`}
              className="text-sm text-slate-700 hover:text-slate-900 transition-colors"
            >
              {t(`header.nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div
            role="group"
            aria-label={t("languageSwitcher.label")}
            className="flex items-center rounded-sm border border-slate-200 overflow-hidden"
          >
            <button
              type="button"
              data-testid="lang-switch-ar"
              aria-pressed={lang === "ar"}
              onClick={() => changeLanguage("ar")}
              className={`px-3 py-1.5 text-xs font-medium ${
                lang === "ar"
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {t("languageSwitcher.es")}
            </button>
            <button
              type="button"
              data-testid="lang-switch-en"
              aria-pressed={lang === "en"}
              onClick={() => changeLanguage("en")}
              className={`px-3 py-1.5 text-xs font-medium ${
                lang === "en"
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {t("languageSwitcher.en")}
            </button>
          </div>
          <Button
            asChild
            data-testid="header-cta"
            className="rounded-sm bg-[#C25934] hover:bg-[#a94a2c] text-white"
          >
            <a
              href={buildPath(lang, "#newsletter")}
              onClick={(e) => handleNavClick(e, buildPath(lang, "#newsletter"))}
            >
              {t("header.cta")}
            </a>
          </Button>
        </div>

        <button
          type="button"
          data-testid="header-menu-toggle"
          aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm border border-slate-200 text-slate-800"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-6 py-6 space-y-4">
            <nav className="flex flex-col gap-4">
              {nav.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  data-testid={`mobile-nav-${item.key}`}
                  className="text-base text-slate-800"
                >
                  {t(`header.nav.${item.key}`)}
                </a>
              ))}
            </nav>
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <button
                type="button"
                data-testid="mobile-lang-ar"
                onClick={() => changeLanguage("ar")}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm border ${
                  lang === "ar"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200"
                }`}
              >
                {t("languageSwitcher.es")}
              </button>
              <button
                type="button"
                data-testid="mobile-lang-en"
                onClick={() => changeLanguage("en")}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm border ${
                  lang === "en"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200"
                }`}
              >
                {t("languageSwitcher.en")}
              </button>
            </div>
            <Button
              asChild
              data-testid="mobile-header-cta"
              className="w-full rounded-sm bg-[#C25934] hover:bg-[#a94a2c] text-white"
            >
              <a
                href={buildPath(lang, "#newsletter")}
                onClick={(e) => handleNavClick(e, buildPath(lang, "#newsletter"))}
              >
                {t("header.cta")}
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
